import { axiosClient } from '../axiosClient';

export interface DashboardMetrics {
  onlineNow: number;
  totalDevices: number;
  activeDevices: number;
  totalErrors: number;
  criticalErrors: number;
  totalUsers: number;
  activeUsers: number;
  unresolvedErrors: number;
}

export const getDashboardMetrics = async (): Promise<DashboardMetrics> => {
  try {
    const response = await axiosClient.get('/dashboard/metrics');
    const data = response.data;

    return {
      onlineNow: data.online_now ?? 0,
      totalDevices: data.total_devices,
      activeDevices: data.active_devices,
      totalErrors: data.total_errors || 0,
      criticalErrors: data.critical_errors,
      totalUsers: data.total_users,
      activeUsers: data.active_users,
      unresolvedErrors: data.unresolved_errors,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metrics:', error);
    throw error;
  }
};

export interface ActiveUsersWindows {
  active1d: number;
  active1dPrevious: number;
  active7d: number;
  active15d: number;
  active30d: number;
}

export const getActiveUsersWindows = async (): Promise<ActiveUsersWindows> => {
  try {
    const response = await axiosClient.get('/dashboard/active-users');
    const data = response.data;

    return {
      active1d: data.active_1d,
      active1dPrevious: data.active_1d_previous,
      active7d: data.active_7d,
      active15d: data.active_15d,
      active30d: data.active_30d,
    };
  } catch (error) {
    console.error('Failed to fetch active users windows:', error);
    throw error;
  }
};

export type ActivityPeriod = '7d' | '30d' | '3m';

export interface DeviceActivityPoint {
  date: string;
  devices: number;
}

/** Aparelhos distintos com uso em cada dia (Brasília). */
export const getDeviceActivity = async (period: ActivityPeriod = '7d') => {
  try {
    const response = await axiosClient.get<DeviceActivityPoint[]>('/dashboard/device-activity', {
      params: { period },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch device activity:', error);
    throw error;
  }
};

export interface FirebaseAnalyticsSnapshot {
  /** Dia (YYYY-MM-DD) a que os números se referem — o GA4 atrasa 1–2 dias. */
  referenceDate: string | null;
  installs24h: number;
  uninstalls24h: number;
  uninstallsPlatform: string;
  sessions24h: number;
  /** null = dia anterior sem dado pra comparar. */
  sessionsChange: number | null;
  events24h: number;
  crashFreeRate: number;
}

export const getFirebaseAnalytics = async (): Promise<FirebaseAnalyticsSnapshot> => {
  try {
    const response = await axiosClient.get('/dashboard/firebase-analytics');
    const data = response.data;

    return {
      referenceDate: data.reference_date ?? null,
      installs24h: data.installs_24h,
      uninstalls24h: data.uninstalls_24h,
      uninstallsPlatform: data.uninstalls_platform,
      sessions24h: data.sessions_24h,
      sessionsChange: data.sessions_change,
      events24h: data.events_24h,
      crashFreeRate: data.crash_free_rate,
    };
  } catch (error) {
    console.error('Failed to fetch Firebase analytics:', error);
    throw error;
  }
};

export const getErrorsByType = async () => {
  try {
    const response = await axiosClient.get('/dashboard/errors-by-type');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch errors by type:', error);
    throw error;
  }
};

interface RecentDeviceItem {
  id: number;
  status: string;
  user: string;
  user_id: number;
  user_created_at: string | null;
  device: string;
  platform: string;
  app_version: string;
  last_activity: string;
}

export const getRecentDevices = async (limit = 10) => {
  try {
    const response = await axiosClient.get<RecentDeviceItem[]>('/dashboard/recent-devices', {
      params: { limit },
    });
    const data = response.data;

    const devices = data.map((item) => {
      const [model, ...platformParts] = item.device.split(' ');
      const platform = platformParts.join(' ');

      return {
        id: item.id.toString(),
        status: item.status,
        userName: item.user,
        userId: item.user_id.toString(),
        userCreatedAt: item.user_created_at,
        model: model,
        platform: item.platform,
        appVersion: item.app_version,
        lastActivity: item.last_activity,
        sessions: 0,
        errors: 0,
        crashes: 0,
        osVersion: platform,
        fingerprint: '',
      };
    });

    return { devices };
  } catch (error) {
    console.error('Failed to fetch recent devices:', error);
    throw error;
  }
};
